import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { GridsterItem } from 'angular-gridster2';

/**
 * Interface for widget layout in dashboard
 */
export interface WidgetLayoutItem {
  type: string;
  title?: string;
  position: {
    x: number;
    y: number;
    cols: number;
    rows: number;
  };
  dataSource?: string;
  settings?: {
    category?: string;
    filters?: Record<string, any>;
    fields?: string[];
    displayType?: string;
  };
}

/**
 * Interface for dashboard layout
 */
export interface DashboardLayout {
  id: string;
  name: string;
  description?: string;
  widgets: WidgetLayoutItem[];
}

// Default layout defined directly in the service as a JSON object
export const DEFAULT_LAYOUT: DashboardLayout = {
  id: 'default',
  name: "Default Layout",
  description: "Standard dashboard layout with optimized widget placement",
  widgets: [
    {
      type: "status",
      position: {
        x: 0,
        y: 0,
        cols: 2,
        rows: 2
      },
      title: "Status for årlige øvelser",
      dataSource: "status_data",
      settings: {
        category: "status",
        filters: {},
        fields: ["percentComplete"],
        displayType: "chart"
      }
    },
    {
      type: "upcomingExercises",
      position: {
        x: 2,
        y: 0,
        cols: 2,
        rows: 2
      },
      title: "Kommende øvelser",
      dataSource: "upcoming_exercises",
      settings: {
        category: "upcomingExercises",
        filters: {},
        fields: ["name", "date", "location"],
        displayType: "table"
      }
    },
    {
      type: "quickLinks",
      position: {
        x: 4,
        y: 0,
        cols: 2,
        rows: 2
      },
      title: "Hurtige links",
      dataSource: "quick_links",
      settings: {
        category: "quickLinks",
        filters: {},
        fields: ["title", "url"],
        displayType: "buttons"
      }
    },
    {
      type: "numberStats",
      position: {
        x: 6,
        y: 0,
        cols: 2,
        rows: 2
      },
      title: "Nøgletal",
      dataSource: "number_stats",
      settings: {
        category: "numberStats",
        filters: {},
        fields: ["value", "secondaryValue", "percentComplete", "title"],
        displayType: "single"
      }
    },
    {
      type: "userProgress",
      position: {
        x: 0,
        y: 1,
        cols: 4,
        rows: 2
      },
      title: "Bruger fremgang",
      dataSource: "user_progress",
      settings: {
        category: "userProgress",
        filters: { path: "all" },
        fields: ["name", "station", "path", "percentComplete"],
        displayType: "table"
      }
    },
    /*{
      type: "reminders",
      position: {
        x: 4,
        y: 1,
        cols: 3,
        rows: 2
      },
      title: "Påmindelser",
      dataSource: "reminders",
      settings: {
        category: "reminders",
        filters: { status: "pending" },
        fields: ["title", "dueDate", "completed"],
        displayType: "list"
      }
    },*/
    {
      type: "yearComparison",
      position: {
        x: 0,
        y: 2,
        cols: 6,
        rows: 2
      },
      title: "Årlig sammenligning",
      dataSource: "year_comparison",
      settings: {
        category: "yearComparison",
        filters: { metricType: "all" },
        fields: ["label", "currentYear", "previousYear"],
        displayType: "chart"
      }
    }
  ]
};

/**
 * Simple service to manage dashboard layouts
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardLayoutService {
  private apiUrl = environment.apiUrl;
  private layouts = new Map<string, DashboardLayout>();
  
  // Default layouts
  private readonly defaultLayouts: Record<string, DashboardLayout> = {
    'default': {
      id: 'default',
      name: 'Default Layout',
      description: 'Standard dashboard layout with user progress widgets',
      widgets: [
        // User Progress Widget 1 - Indsatsleder
        {
          type: 'userProgress',
          title: 'Bruger fremgang - Indsatsleder',
          position: {
            x: 0,
            y: 0,
            cols: 6,
            rows: 6
          },
          dataSource: 'user_progress',
          settings: {
            filters: {
              educationalPathId: 'indsatsleder',
              sortBy: 'percentComplete',
              sortDirection: 'desc'
            },
            displayType: 'table'
          }
        },
        
        // User Progress Widget 2 - FUI
        {
          type: 'userProgress',
          title: 'Bruger fremgang - FUI',
          position: {
            x: 6,
            y: 0,
            cols: 6,
            rows: 6
          },
          dataSource: 'user_progress',
          settings: {
            filters: {
              educationalPathId: 'fui',
              sortBy: 'percentComplete',
              sortDirection: 'desc'
            },
            displayType: 'table'
          }
        }
      ]
    },
    
    'admin': {
      id: 'admin',
      name: 'Administrator Layout',
      description: 'Dashboard layout for administrators with system overview widgets',
      widgets: [
        // Status Widget
        {
          type: 'status',
          title: 'System Status',
          position: {
            x: 0,
            y: 0,
            cols: 3,
            rows: 2
          },
          dataSource: 'system_status'
        },
        
        // User Progress Widget - Admin View
        {
          type: 'userProgress',
          title: 'User Progress Overview',
          position: {
            x: 0,
            y: 2,
            cols: 6,
            rows: 6
          },
          dataSource: 'user_progress',
          settings: {
            displayType: 'chart'
          }
        }
      ]
    }
  };

  constructor(
    private http: HttpClient
  ) {
    // Register the default layout directly
    this.layouts.set('default', DEFAULT_LAYOUT);
    
    // Load any saved layouts from localStorage
    this.loadSavedLayouts();
  }

  /**
   * Load a layout from an API endpoint
   * @param apiUrl URL to load the layout from
   * @param layoutId ID to assign to the layout
   */
  loadLayoutFromApi(apiUrl: string, layoutId: string): Observable<DashboardLayout> {
    return this.http.get<DashboardLayout>(apiUrl).pipe(
      tap(layout => {
        this.layouts.set(layoutId, layout);
      }),
      catchError(error => {
        console.error(`[DASHBOARD-LAYOUT] Error loading layout from API:`, error);
        return throwError(() => new Error(`Failed to load layout from API`));
      })
    );
  }

  /**
   * Get a layout by ID
   * @param layoutId Layout ID to retrieve
   */
  getLayout(layoutId: string = 'default'): Observable<DashboardLayout> {
    const layout = this.layouts.get(layoutId);
    
    if (layout) {
      return of(layout);
    }
    
    console.error(`[DASHBOARD-LAYOUT] Layout with ID "${layoutId}" not found. Available layouts:`, Array.from(this.layouts.keys()));
    return throwError(() => new Error(`Layout with ID "${layoutId}" not found`));
  }

  /**
   * Save a layout
   * @param layout Layout to save
   * @param layoutId ID to save the layout under
   */
  saveLayout(layout: DashboardLayout, layoutId: string = 'custom'): void {
    this.layouts.set(layoutId, layout);
    
    // Also save to localStorage for persistence
    try {
      localStorage.setItem(`dashboard_layout_${layoutId}`, JSON.stringify(layout));
    } catch (error) {
      console.error(`[DASHBOARD-LAYOUT] Error saving layout to localStorage:`, error);
    }
  }

  /**
   * Load saved layouts from localStorage
   */
  private loadSavedLayouts(): void {
    try {
      // Look for any layout keys in localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('dashboard_layout_')) {
          const layoutId = key.replace('dashboard_layout_', '');
          const layoutData = localStorage.getItem(key);
          
          if (layoutData) {
            const layout = JSON.parse(layoutData) as DashboardLayout;
            this.layouts.set(layoutId, layout);
          }
        }
      }
    } catch (error) {
      console.error(`[DASHBOARD-LAYOUT] Error loading saved layouts:`, error);
    }
  }

  /**
   * Get all available layout IDs
   */
  getAvailableLayouts(): string[] {
    return Array.from(this.layouts.keys());
  }

  /**
   * Create a layout from current widget positions
   * @param name Name for the layout
   * @param widgets Current widget positions
   * @param description Optional description
   */
  createLayoutFromWidgets(
    name: string, 
    widgets: { type: string; x: number; y: number; cols: number; rows: number }[],
    description?: string
  ): DashboardLayout {
    const layout: DashboardLayout = {
      id: 'custom',
      name,
      description,
      widgets: widgets.map(w => ({
        type: w.type,
        position: {
          x: w.x,
          y: w.y,
          cols: w.cols,
          rows: w.rows
        }
      }))
    };
    
    return layout;
  }

  /**
   * Get a dashboard layout by ID
   * @param id Layout ID
   */
  getDashboardLayout(id: string): Observable<DashboardLayout> {
    // First check for default layouts
    if (id in this.defaultLayouts) {
      return of(this.defaultLayouts[id]);
    }
    
    // Otherwise try to load from API
    return this.http.get<DashboardLayout>(`${this.apiUrl}/dashboard/layouts/${id}`).pipe(
      catchError(() => {
        console.warn(`Layout '${id}' not found, falling back to default layout`);
        return of(this.defaultLayouts['default']);
      })
    );
  }

  /**
   * Get all available layouts
   */
  getAllLayouts(): Observable<DashboardLayout[]> {
    return this.http.get<DashboardLayout[]>(`${this.apiUrl}/dashboard/layouts`).pipe(
      catchError(() => {
        // Fall back to default layouts
        return of(Object.values(this.defaultLayouts));
      })
    );
  }

  /**
   * Create a new layout
   */
  createLayout(layout: DashboardLayout): Observable<DashboardLayout> {
    return this.http.post<DashboardLayout>(`${this.apiUrl}/dashboard/layouts`, layout).pipe(
      catchError(error => {
        console.error('Error creating layout:', error);
        return of(layout); // Return as is for now
      })
    );
  }

  /**
   * Update a layout
   */
  updateLayout(id: string, layout: Partial<DashboardLayout>): Observable<DashboardLayout> {
    return this.http.put<DashboardLayout>(`${this.apiUrl}/dashboard/layouts/${id}`, layout).pipe(
      catchError(error => {
        console.error('Error updating layout:', error);
        // Return a default or original layout
        return of(this.defaultLayouts[id] || { 
          id, 
          name: 'Custom Layout', 
          widgets: [] 
        } as DashboardLayout);
      })
    );
  }

  /**
   * Delete a layout
   */
  deleteLayout(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/dashboard/layouts/${id}`).pipe(
      catchError(error => {
        console.error('Error deleting layout:', error);
        return of({ success: false });
      })
    );
  }

  /**
   * Get available dashboard layouts for the current user
   */
  getAvailableLayoutsForUser(): Observable<any[]> {
    return this.http.get<any>(`${this.apiUrl}/dashboard/layouts`)
      .pipe(
        map(response => response.data || []),
        catchError(() => of([]))
      );
  }

  /**
   * Load a specific dashboard layout by ID
   */
  loadLayout(layoutId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/dashboard/layouts/${layoutId}`)
      .pipe(
        map(response => response.data || null),
        catchError(() => of(null))
      );
  }
} 